import React, { useContext, useMemo } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  SkeletonPage,
  SkeletonBodyText,
  Box,
  Banner,
  Tooltip,
  Icon,
} from '@shopify/polaris';
import { QuestionCircleIcon, ArrowLeftIcon } from '@shopify/polaris-icons';
import { AuthContext } from '../context/AuthContext';
import LayoutWrapper from '../components/Layout/Layout';
import BarChart from '../components/Charts/BarChart';
import useData from '../hooks/useData';
import { cleanText } from '../utils/cleanText'; // Assuming cleanText is in utils
import PrescriptionBanner from '../components/Prescriptions/PrescriptionBanner';
import '../components/Layout/ui-shell.css';

// --- PageGoal component with plain text ---
function PageGoal({ goal }) {
  return (
    <Text as="p" tone="subdued">
      <Text as="span" fontWeight="bold">{cleanText("Page Goal:")}</Text> {cleanText(goal)}
    </Text>
  );
}
// --- end PageGoal component ---

const API = process.env.REACT_APP_API_URL || 'https://api.baobrain.com';

// --- Page classifier (keep all your existing helper functions) ---
const classifyPage = (p = '') => {
  const s = (p || '').toLowerCase().trim();
  if (s === '/' || s === 'landing' || s.includes('/home') || s.includes('/landing')) return 'home';  // No cleanText
  if (s === 'view' || s.includes('pdp') || s.includes('/product') || s.includes('/p/') || s.includes('product')) return 'product';
  if (s.includes('plp') || s.includes('category') || s.includes('/collections') || s.includes('/catalog') || s.includes('/shop') || s.includes('collection')) return 'collection';
  if (s.includes('/search') || s === 'search') return 'search';
  if (s.includes('mini-cart') || s.includes('minicart') || s.includes('cart-drawer') || s.includes('drawer')) return 'cart_drawer';
  if (s === 'cart' || s.includes('/cart')) return 'cart';
  if (s === 'checkout' || s.includes('/checkout')) return 'checkout';
  if (s.includes('/payment') || s.includes('payment')) return 'payment';
  if (s.includes('thank') || s.includes('confirm') || s.includes('success') || s.includes('purchase') || s.includes('order-confirmation')) return 'confirmation';
  if (s.includes('/blog') || s.includes('/article')) return 'blog';
  if (s === 'contact' || s.includes('/pages/contact') || s.includes('/contact')) return 'contact';
  if (s === 'about' || s.includes('/pages/about') || s.includes('/about')) return 'about';
  if (s.includes('returns') || s.includes('shipping') || s.includes('delivery') || s.includes('warranty') || s.includes('guarantee')) return 'info';
  if (s.includes('/policies') || s.includes('/terms') || s.includes('privacy')) return 'policy';
  if (s.includes('login') || s.includes('register') || s.includes('account')) return 'auth';
  if (s.includes('subscribe') || s.includes('newsletter') || s.includes('email-signup')) return 'email';
  if (s.includes('promo') || s.includes('sale') || s.includes('discount')) return 'promo';
  if (s.includes('upsell') || s.includes('bundle') || s.includes('recommended')) return 'upsell';
  if (s.includes('faq') || s.includes('/help') || s.includes('/support')) return 'faq';
  return 'other';
};

const getDropoffInsights = (page, metrics) => {
  const dropoffPct = metrics.dropoff_pct ?? metrics.percent_of_total ?? metrics.dropoff_rate_pct ?? 0;
  const exits = metrics.exits || metrics.count || 0;
  const pageviews = metrics.pageviews || metrics.count || 0;
  const pageType = classifyPage(page);
  
  const avgOrderValue = 75;
  const lostRevenue = Math.round(exits * 0.02 * avgOrderValue); // Assume 2% would convert

  const insights = {
    home: {
      problem: `${Math.round(dropoffPct)}% leave immediately (${exits} people)`,
      why: dropoffPct > 60 ? "Homepage doesn't show what you sell fast enough" : "Visitors aren't finding products",
      symptoms: [
        dropoffPct > 70 && `${Math.round(dropoffPct)}% is 2x industry avg (40%)`,
        lostRevenue > 50 && `~$${lostRevenue}/mo lost`
      ].filter(Boolean),
      fixes: [
        { action: "Hero section: Show bestseller + 'Shop Now' button", impact: "+25% clicks" },
        { action: "Add trust badge above fold", impact: "+15% engagement" }
      ]
    },
    
    product: {
      problem: `${Math.round(dropoffPct)}% view but don't add to cart (${exits} lost)`,
      why: "Missing info, unclear price, or no social proof",
      symptoms: [
        dropoffPct > 85 && `${Math.round(dropoffPct)}% is bad (avg: 70-75%)`,
        metrics.rage_clicks > 0 && `${metrics.rage_clicks} rage clicks`,
        metrics.dead_clicks > 0 && `${metrics.dead_clicks} dead clicks`
      ].filter(Boolean),
      fixes: [
        { action: "Add reviews near Add to Cart", impact: "+270% conversion" },
        { action: "Show shipping cost on page", impact: "-18% abandonment" },
        { action: "Add stock urgency ('Only 3 left')", impact: "+15% conversion" }
      ]
    },
    
    cart: {
      problem: `${Math.round(dropoffPct)}% abandon cart (${exits} people)`,
      why: "Unexpected costs or forced account creation",
      symptoms: [
        dropoffPct > 75 && `${Math.round(dropoffPct)}% vs 69% avg`,
        lostRevenue > 100 && `~$${lostRevenue}/mo lost`
      ].filter(Boolean),
      fixes: [
        { action: "Add Guest Checkout option", impact: "+25-30% completions" },
        { action: "Show shipping in cart, not checkout", impact: "-20% abandonment" },
        { action: "Exit popup: '10% off if you buy now'", impact: "+10-15% recovery" }
      ]
    },
    
    checkout: {
      problem: `${Math.round(dropoffPct)}% abandon at checkout (${exits} lost sales)`,
      why: "Too many steps, no express pay, or trust issues",
      symptoms: [
        dropoffPct > 50 && `${Math.round(dropoffPct)}% is high (avg: 30-40%)`,
        exits > 0 && `$${Math.round(exits * avgOrderValue)} lost`
      ].filter(Boolean),
      fixes: [
        { action: "Enable Apple/Google/Shop Pay", impact: "2-3x mobile conversion" },
        { action: "Remove optional form fields", impact: "+30% per field removed" },
        { action: "Add progress indicator + trust badges", impact: "-15% anxiety" }
      ]
    },
    
    collection: {
      problem: `${Math.round(dropoffPct)}% leave without clicking a product`,
      why: "Can't find what they want or products don't look appealing",
      symptoms: [
        dropoffPct > 70 && `${Math.round(dropoffPct)}% exit rate`,
        pageviews > 50 && `${pageviews} views, low engagement`
      ].filter(Boolean),
      fixes: [
        { action: "Add filters (size, color, price)", impact: "2x click rate" },
        { action: "Show bestsellers first", impact: "+30% clicks" }
      ]
    },

    cart_drawer: {
      problem: `${Math.round(dropoffPct)}% close cart without checkout`,
      why: "Cart drawer doesn't encourage next step",
      symptoms: [
        dropoffPct > 80 && `${Math.round(dropoffPct)}% is high`
      ].filter(Boolean),
      fixes: [
        { action: "Add 'Checkout Now' button prominently", impact: "+20% to checkout" },
        { action: "Show free shipping progress bar", impact: "+15% AOV" }
      ]
    },

    search: {
      problem: `${Math.round(dropoffPct)}% leave after searching`,
      why: "Search results aren't relevant or empty",
      symptoms: [
        dropoffPct > 60 && `${Math.round(dropoffPct)}% exit from search`
      ].filter(Boolean),
      fixes: [
        { action: "Check top search terms with no results", impact: "Find product gaps" },
        { action: "Add search suggestions/autocomplete", impact: "+25% search success" }
      ]
    }
  };
  
  return insights[pageType] || {
    problem: `${Math.round(dropoffPct)}% exit rate (${exits} people)`,
    why: "Page may not have clear next step",
    symptoms: [
      pageviews > 0 && `${pageviews} views`,
      exits > 0 && `${exits} exits`
    ].filter(Boolean),
    fixes: [
      { action: "Add clear CTA button", impact: "Reduce confusion" },
      { action: "Check page load speed", impact: "-7% conversion per second delay" }
    ]
  };
};
function computePainScore(m = {}) {
  const signals = [];
  const pct = m.dropoff_pct ?? m.percent_of_total ?? m.dropoff_rate_pct ?? 0;
  const add = (present, weight, val) => present ? signals.push({ w: weight, v: val }) : null;
  add(pct != null, 0.35, pct);
  add(m.scroll_pct != null, 0.20, 100 - m.scroll_pct);
  add(m.click_pct != null, 0.15, 100 - m.click_pct);
  add(m.bounce_rate != null, 0.15, m.bounce_rate);
  add(m.page_load_time_ms != null, 0.15,
      Math.max(0, Math.min(100, ((m.page_load_time_ms / 1000) - 2.5) * 40)));
  const sumW = signals.reduce((a, s) => a + s.w, 0) || 1;
  return Math.round(signals.reduce((a, s) => a + (s.w / sumW) * (s.v || 0), 0));
}

export default function DropOffsPage() {
  const { token } = useContext(AuthContext);
  const siteId = sessionStorage.getItem('site_id');
  const { data: sites = [], loading: sitesLoading, error: sitesError } = useData(
    !siteId && token ? `sites:${token}` : 'noop',
    () => fetch(`${API}/api/sites`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      if (!res.ok) throw new Error(cleanText(`Failed to fetch sites: Status ${res.status}`));
      return res.json();
    }),
    { ttlMs: 24 * 60 * 60 * 1000, deps: [token] }
  );

  const actualSiteId = useMemo(() => {
    if (siteId) return siteId;
    if (sites.length > 0) {
      const newSiteId = String(sites[0].id);
      sessionStorage.setItem('site_id', newSiteId);
      return newSiteId;
    }
    return null;
  }, [siteId, sites]);

  const { data: salesSummaryData, loading: salesLoading, error: salesError } = useData(
    actualSiteId ? `dropoffsSummary:${actualSiteId}` : 'noop',
    () => fetch(`${API}/api/dropoffs/summary?site_id=${actualSiteId}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => {
      if (!r.ok) throw new Error(cleanText(r.statusText));
      return r.json();
    }),
    //{ ttlMs: 24 * 60 * 60 * 1000, deps: [actualSiteId, token] }
    { ttlMs: 60 * 60 * 1000, deps: [actualSiteId, token] }
  );

  const { data: ciFunnel, loading: ciFunnelLoading, error: ciFunnelError } = useData(
    actualSiteId ? `customer-intel:funnel:${actualSiteId}:30` : 'noop',
    async () => {
      if (!actualSiteId || !token) return null;
      const r = await fetch(
        `${API}/api/customer-intelligence/funnel?site_id=${actualSiteId}&days=30`,
        { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' }
      );
      return r.ok ? r.json() : null;
    },
    //{ ttlMs: 24 * 60 * 60 * 1000, deps: [actualSiteId, token] }
    { ttlMs: 60 * 60 * 1000, deps: [actualSiteId, token] }
  );

  const { data: allFunnelSteps = [], loading: uxLoading, error: uxError } = useData(
    actualSiteId ? `dropoffsAnalytics:${actualSiteId}` : 'noop',
    () => fetch(`${API}/analytics/dropoffs?site_id=${actualSiteId}&limit=10`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => {
      if (!r.ok) throw new Error(cleanText(r.statusText));
      return r.json();
    }),
    //{ ttlMs: 24 * 60 * 60 * 1000, deps: [actualSiteId, token] }
    { ttlMs: 60 * 60 * 1000, deps: [actualSiteId, token] }
  );

  const { salesFunnelDropoffs, userExperienceDropoffs } = useMemo(() => {
    const sales = salesSummaryData?.conversion_funnel || [];
    const ux = [];
    const steps = Array.isArray(allFunnelSteps) ? allFunnelSteps : [];
    const salesFunnelPaths = new Set(sales.map(s => String(s.step ?? s.page ?? '').toLowerCase()));
    steps.forEach(step => {
      const path = step.page_path ?? step.step ?? step.page;
      const lowerPath = String(path).toLowerCase();
      if (!salesFunnelPaths.has(lowerPath) && path) {
        ux.push({ ...step, page: path });
      }
    });
    return { salesFunnelDropoffs: sales, userExperienceDropoffs: ux };
  }, [allFunnelSteps, salesSummaryData]);

  const allDropoffData = useMemo(() => {
    return [...salesFunnelDropoffs, ...userExperienceDropoffs];
  }, [salesFunnelDropoffs, userExperienceDropoffs]);

  const sortedSteps = useMemo(() => {
    return allDropoffData.slice().sort((a, b) => {
      const aScore = computePainScore(a);
      const bScore = computePainScore(b);
      return bScore - aScore;
    });
  }, [allDropoffData]);

  const uxBarChartData = useMemo(() => {
    // Use sortedSteps which combines all dropoff data
    const dataSource = sortedSteps.length > 0 ? sortedSteps : userExperienceDropoffs;
    return dataSource.slice(0, 10).map(step => ({
      name: (step.page ?? step.page_path ?? step.step ?? 'Unknown').replace(/^\//, '').slice(0, 20) || 'Home',
      value: step.dropoff_pct ?? step.percent_of_total ?? step.dropoff_rate_pct ?? computePainScore(step),
    }));
  }, [sortedSteps, userExperienceDropoffs]);

  const barChartHeight = 320;
  
  const loading = sitesLoading || salesLoading || uxLoading || ciFunnelLoading;
  const error = sitesError || salesError || uxError || ciFunnelError;

  if (!token) {
    return (
      <LayoutWrapper>
        <Page title={cleanText("Drop-Off Points")}>
          <Layout>
            <Layout.Section>
              <Banner status="critical">{cleanText("Authentication Required: Please log in to view this page.")}</Banner>
            </Layout.Section>
          </Layout>
        </Page>
      </LayoutWrapper>
    );
  }
  if (loading || !actualSiteId) {
    return (
      <LayoutWrapper>
        <Page title={cleanText("Drop-Off Points")}>
          <SkeletonPage title={cleanText("Drop-Off Points")}>
            <SkeletonBodyText />
          </SkeletonPage>
        </Page>
      </LayoutWrapper>
    );
  }
  if (error) {
    return (
      <LayoutWrapper>
        <Page title={cleanText("Drop-Off Points")}>
          <Layout>
            <Layout.Section>
              <Banner status="critical">{cleanText(`Error: ${error.message || error}`)}</Banner>
            </Layout.Section>
          </Layout>
        </Page>
      </LayoutWrapper>
    );
  }
  return (
    <LayoutWrapper>
      <Page 
        title={cleanText("Drop-Off Points")}
        backAction={{
          content: cleanText('Back'),
          onAction: () => window.history.back(),
          icon: ArrowLeftIcon,
        }}
      >
        <PageGoal goal={cleanText("Identify the pages where most visitors leave your site, and get actionable insights to fix them.")} />
        <div style={{
          height: 4, width: '100%', borderRadius: 4, margin: '4px 0 12px',
          background: 'linear-gradient(90deg, var(--accent), var(--accent-2))'
        }} />
         {/* Prescription Banner for conversion issues */}
         <PrescriptionBanner 
          siteId={actualSiteId}
          token={token}
          category="conversion"
        />

        <Layout>
          {sortedSteps.length > 0 && (
            <Layout.Section>
              <Card title={cleanText("Your Biggest Visitor Leaks")} sectioned>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                  <div style={{
                    padding: 20,
                    borderRadius: 12,
                    background: 'rgba(106, 13, 173, 0.1)',
                    border: '1px solid rgba(106, 13, 173, 0.3)'
                  }}>
                    <div style={{ fontSize: 24, fontWeight: 'bold', color: 'var(--accent)', marginBottom: 4 }}>
                      {(sortedSteps.filter(s => (s.dropoff_pct ?? s.percent_of_total ?? s.dropoff_rate_pct ?? 0) > 50).length.toString())}
                    </div>
                    <Text variant="bodyMd" as="h3">{("Critical Issues")}</Text>
                    <Text variant="bodySm" color="subdued">{cleanText("Pages losing > 50% of visitors")}</Text>
                  </div>
                  <div style={{
                    padding: 20,
                    borderRadius: 12,
                    background: 'rgba(0, 168, 169, 0.1)',
                    border: '1px solid rgba(0, 168, 169, 0.3)'
                  }}>
                    <div style={{ fontSize: 24, fontWeight: 'bold', color: 'var(--accent-2)', marginBottom: 4 }}>
                      {(sortedSteps.reduce((sum, s) => sum + (s.exits || 0), 0).toLocaleString())}
                    </div>
                    <Text variant="bodyMd" as="h3">{("Visitors Lost")}</Text>
                    <Text variant="bodySm" color="subdued">{cleanText("Total exits from key pages")}</Text>
                  </div>
                  <div style={{
                    padding: 20,
                    borderRadius: 12,
                    background: 'rgba(106, 13, 173, 0.1)',
                    border: '1px solid rgba(106, 13, 173, 0.3)'
                  }}>
                    <div style={{ fontSize: 24, fontWeight: 'bold', color: 'var(--accent)', marginBottom: 4 }}>
                      {cleanText(sortedSteps.length > 0
                        ? `${Math.round(sortedSteps.reduce((sum, s) => sum + (s.dropoff_pct ?? s.percent_of_total ?? s.dropoff_rate_pct ?? 0), 0) / sortedSteps.length)}%`
                        : "0%")}
                    </div>
                    <Text variant="bodyMd" as="h3">{("Average Dropoff Rate")}</Text>
                    <Text variant="bodySm" color="subdued">{cleanText("Across all tracked pages")}</Text>
                  </div>
                </div>
              </Card>
            </Layout.Section>
          )}
          <Layout.Section>
            <div className="grid grid-2">
              <Card sectioned>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <h3 className="chart-title">{("Sales Funnel Performance")}</h3>
                  <Tooltip content={cleanText("A sales funnel chart shows how visitors move step by step toward purchase, and where they drop off.")}>
                    <Icon source={QuestionCircleIcon} color="subdued" />
                  </Tooltip>
                </div>
                <Box padding="4" className="card--brand-purple">
                  {ciFunnel?.conversion_funnel?.steps?.length ? (
                    <div style={{ padding: '12px 0' }}>
                      {ciFunnel.conversion_funnel.steps.map((s, i) => (
                        <div key={i} style={{ marginBottom: 16 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <Text variant="bodyMd" fontWeight="semibold">{cleanText(s.name)}</Text>
                            <Text variant="bodyMd" color="subdued">{cleanText((s.count ?? 0).toLocaleString())}</Text>
                          </div>
                          <div style={{
                            height: 8,
                            background: '#e5e7eb',
                            borderRadius: 6,
                            overflow: 'hidden'
                          }}>
                            <div
                              style={{
                                width: `${s.rate_from_prev_pct || 0}%`,
                                height: '100%',
                                background: s.rate_from_prev_pct > 50 ? 'var(--accent-2)' :
                                            s.rate_from_prev_pct > 25 ? 'var(--accent)' : 'var(--accent)'
                              }}
                            />
                          </div>
                          {s.rate_from_prev_pct < 50 && i > 0 && (
                            <Text variant="bodySm" tone="critical" as="p" style={{ marginTop: 4 }}>
                              {cleanText(`⚠ Drop-off: ${(100 - s.rate_from_prev_pct).toFixed(1)}% lost`)}
                            </Text>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Text variant="bodyMd" color="subdued">{cleanText("No funnel data available")}</Text>
                  )}
                </Box>
              </Card>
              <Card sectioned>
                <h3 className="chart-title">{("Drop-Offs by Page")}</h3>
                <Box padding="4" className="card--brand-teal">
                  <div style={{ height: barChartHeight, width: '100%' }}>
                    {uxBarChartData.length > 0 ? (
                      <BarChart data={uxBarChartData} />
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Text variant="bodyMd" color="subdued">No dropoff data available yet</Text>
                      </div>
                    )}
                  </div>
                </Box>
              </Card>
            </div>
          </Layout.Section>
          {sortedSteps.length > 0 && (
            <Layout.Section>
              <Card title="Priority Issues" sectioned>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {sortedSteps.slice(0, 3).map((step, idx) => {
                    const insights = getDropoffInsights(step.page || step.step || step.page_path, step);
                    const dropoffPct = step.dropoff_pct ?? step.percent_of_total ?? step.dropoff_rate_pct ?? 0;
                    const isCritical = dropoffPct > 50;
                    
                    return (
                      <div key={idx} style={{
                        padding: 16,
                        borderRadius: 8,
                        background: isCritical ? 'rgba(106, 13, 173, 0.08)' : '#f9fafb',
                        borderLeft: `4px solid ${isCritical ? 'var(--accent)' : 'var(--accent-2)'}`
                      }}>
                        {/* Header: Page + Problem */}
                        <div style={{ marginBottom: 8 }}>
                          <Text variant="headingSm" fontWeight="bold">
                            {step.page || step.step || step.page_path}
                          </Text>
                          <Text variant="bodyMd" tone="critical" as="p" style={{ marginTop: 4 }}>
                            {insights.problem}
                          </Text>
                        </div>
                        
                        {/* Why - single line */}
                        <Text variant="bodySm" color="subdued" as="p" style={{ marginBottom: 12 }}>
                          {insights.why}
                        </Text>
                        
                        {/* Fixes - compact */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {insights.fixes.slice(0, 2).map((fix, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Text variant="bodySm">→ {fix.action}</Text>
                              <span style={{
                                padding: '2px 6px',
                                borderRadius: 4,
                                background: 'rgba(0, 168, 169, 0.15)',
                                color: 'var(--accent-2)',
                                fontSize: 11,
                                fontWeight: 600,
                                whiteSpace: 'nowrap'
                              }}>
                                {fix.impact}
                              </span>
                            </div>
                          ))}
                        </div>
                        
                        {/* Rage/Dead clicks - only if present */}
                        {(step.rage_clicks > 0 || step.dead_clicks > 0) && (
                          <div style={{ marginTop: 8, display: 'flex', gap: 12 }}>
                            {step.rage_clicks > 0 && (
                              <Text variant="bodySm" tone="critical">🔴 {step.rage_clicks} rage clicks</Text>
                            )}
                            {step.dead_clicks > 0 && (
                              <Text variant="bodySm" tone="warning">⚠️ {step.dead_clicks} dead clicks</Text>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Card>
            </Layout.Section>
          )}
        </Layout>
      </Page>
    </LayoutWrapper>
  );
}